import React, { useState } from "react";
import confetti from "canvas-confetti";
import { Customer, Plan, Address } from "../types";
import { AppliedPromo, validatePromo } from "../config/promos";
import { REFERRAL_SOURCES } from "../config/referralSources";
import daveSmile from "../assets/images/dave_smile.svg";

/**
 * Fire a celebratory "realistic" confetti burst (inspired by
 * kirilv.com/canvas-confetti). Uses CRISP brand colours.
 */
const celebratePromo = () => {
  const colors = ["#FF6B35", "#FF8C42", "#0077C8", "#00A8E8", "#ffffff"];
  const defaults = { origin: { y: 0.7 }, colors, zIndex: 9999 };

  const fire = (particleRatio: number, opts: confetti.Options) => {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(200 * particleRatio),
    });
  };

  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

interface DetailsStepProps {
  customer: Customer;
  updateCustomer: (field: keyof Customer, value: string | boolean) => void;
  onSubmit: () => void;
  onBack: () => void;
  loading: boolean;
  selectedPlan: Plan | null;
  selectedAddress: Address | null;
  promoCode: string;
  appliedPromo: AppliedPromo | null;
  onApplyPromo: (code: string, promo: AppliedPromo) => void;
  onRemovePromo: () => void;
  /** True when both NBN and CRISP failed — capture details for a manual check, no plans/pricing. */
  manualQualification?: boolean;
}

interface ValidationErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

const DetailsStep: React.FC<DetailsStepProps> = ({
  customer,
  updateCustomer,
  onSubmit,
  onBack,
  loading,
  selectedPlan,
  selectedAddress,
  promoCode,
  appliedPromo,
  onApplyPromo,
  onRemovePromo,
  manualQualification = false,
}) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [promoInput, setPromoInput] = useState(promoCode || "");
  const [promoError, setPromoError] = useState<string | null>(null);

  const formatCurrency = (value: number): string =>
    `$${value.toFixed(2).replace(/\.00$/, "")}`;

  const handleApplyPromo = () => {
    const result = validatePromo(promoInput, selectedPlan);
    if (result.ok) {
      setPromoError(null);
      onApplyPromo(result.promo.code, result.promo);
      celebratePromo();
    } else {
      setPromoError(result.error);
    }
  };

  const handleRemovePromo = () => {
    setPromoInput("");
    setPromoError(null);
    onRemovePromo();
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, customer[field as keyof Customer] as string);
  };

  const validateField = (field: string, value: string): boolean => {
    let error = "";

    switch (field) {
      case "first_name":
        if (!value.trim()) {
          error = "First name is required";
        } else if (value.trim().length < 2) {
          error = "First name must be at least 2 characters";
        } else if (value.trim().length > 50) {
          error = "First name must be less than 50 characters";
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          error =
            "First name can only contain letters, spaces, hyphens and apostrophes";
        }
        break;

      case "last_name":
        if (!value.trim()) {
          error = "Last name is required";
        } else if (value.trim().length < 2) {
          error = "Last name must be at least 2 characters";
        } else if (value.trim().length > 50) {
          error = "Last name must be less than 50 characters";
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          error =
            "Last name can only contain letters, spaces, hyphens and apostrophes";
        }
        break;

      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (
          !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
        ) {
          error = "Please enter a valid email address";
        }
        break;

      case "phone":
        const phoneDigits = value.replace(/\D/g, "");
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (!/^04\d{8}$/.test(phoneDigits)) {
          error =
            "Please enter a valid Australian mobile number (04XX XXX XXX)";
        }
        break;
    }

    setErrors((prev: ValidationErrors) => ({
      ...prev,
      [field]: error || undefined,
    }));

    return !error;
  };

  const validateAllFields = (): boolean => {
    const fieldsToValidate = ["first_name", "last_name", "email", "phone"];
    let isValid = true;

    fieldsToValidate.forEach((field) => {
      const valid = validateField(
        field,
        customer[field as keyof Customer] as string
      );
      if (!valid) isValid = false;
    });

    setTouched({
      first_name: true,
      last_name: true,
      email: true,
      phone: true,
    });

    if (!customer.terms_accepted) {
      isValid = false;
    }

    return isValid;
  };

  const handleFieldChange = (field: keyof Customer, value: string) => {
    updateCustomer(field, value);

    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev: ValidationErrors) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateAllFields()) {
      onSubmit();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-crisp-orange">
      {/* Header with Dave */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-crisp-orange mb-2">
            Your Details
          </h2>
          <p className="text-gray-700">
            {manualQualification
              ? "Leave your details and our team will manually check availability at your address and get back to you."
              : "Please provide your contact information so we can get in touch."}
          </p>
        </div>
        <img
          src={daveSmile}
          alt="Dave smiling"
          className="w-32 h-32 ml-4 flex-shrink-0"
        />
      </div>

      {/* Manual qualification notice — shown when automated checks couldn't confirm service */}
      {manualQualification && (
        <div className="bg-blue-50 border-l-4 border-crisp-blue rounded-r-lg p-5 mb-6">
          <h3 className="font-bold text-crisp-dark-blue mb-1">
            Manual qualification required
          </h3>
          <p className="text-sm text-gray-700">
            We couldn't automatically confirm a connection at your address. There's no need to
            choose a plan right now — just leave your details and our team will manually review
            availability and contact you with your options.
          </p>
        </div>
      )}

      {/* Order Summary */}
      <div className="bg-orange-50 border-2 border-crisp-orange rounded-lg p-6 mb-6">
        <h3 className="font-bold text-crisp-orange mb-3">
          {manualQualification ? "Enquiry Summary" : "Order Summary"}
        </h3>
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-gray-600">Address:</span>
            <span className="ml-2 font-medium">
              {selectedAddress?.formatted_address}
            </span>
          </div>
          {selectedPlan && (
            <>
              <div>
                <span className="text-gray-600">Plan:</span>
                <span className="ml-2 font-medium">{selectedPlan.name}</span>
              </div>
              <div>
                <span className="text-gray-600">Price:</span>
                {appliedPromo && appliedPromo.discountedPrice != null ? (
                  <span className="ml-2 font-medium">
                    <span className="text-gray-500 line-through mr-2">
                      {selectedPlan.price_formatted}
                    </span>
                    <span className="text-crisp-orange font-bold">
                      {formatCurrency(appliedPromo.discountedPrice)}/month
                    </span>
                    <span className="text-gray-600 text-xs ml-1">
                      for first {appliedPromo.durationMonths} months, then{" "}
                      {selectedPlan.price_formatted}/month
                    </span>
                  </span>
                ) : (
                  <span className="ml-2 font-medium text-crisp-orange">
                    {selectedPlan.price_formatted}/month {selectedPlan.price_note}
                  </span>
                )}
              </div>
              {selectedPlan.is_fttp_upgrade && (
                <div className="mt-2 bg-blue-50 border-l-4 border-crisp-blue rounded-r p-2">
                  <span className="font-bold text-crisp-dark-blue">FTTP Upgrade requested</span>
                  <span className="ml-2 text-gray-700">
                    — CRISP will arrange the free FTTP upgrade with NBN before activating your service (12 month commitment).
                  </span>
                </div>
              )}

              {/* Promo code */}
              <div className="mt-3 pt-3 border-t border-crisp-orange/30">
                {appliedPromo ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div>
                      <span className="font-bold text-green-700">
                        🎟️ {appliedPromo.code} applied
                      </span>
                      <span className="ml-2 text-gray-700">
                        — {appliedPromo.label}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePromo}
                      disabled={loading}
                      className="text-sm text-gray-500 hover:text-red-600 underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="promo_code"
                      className="block text-gray-600 mb-1"
                    >
                      Have a promo code?
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="promo_code"
                        type="text"
                        value={promoInput}
                        onChange={(e) => {
                          setPromoInput(e.target.value.toUpperCase());
                          if (promoError) setPromoError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleApplyPromo();
                          }
                        }}
                        placeholder="Enter promo code"
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-crisp-orange focus:ring-2 focus:ring-crisp-orange uppercase"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={handleApplyPromo}
                        disabled={loading || !promoInput.trim()}
                        className="bg-crisp-orange text-white font-bold px-5 py-2 rounded-lg hover:bg-opacity-90 transition disabled:opacity-50"
                      >
                        Apply
                      </button>
                    </div>
                    {promoError && (
                      <p className="mt-1 text-sm text-red-600" role="alert">
                        {promoError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label
              htmlFor="first_name"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="first_name"
              type="text"
              value={customer.first_name}
              onChange={(e) => handleFieldChange("first_name", e.target.value)}
              onBlur={() => handleBlur("first_name")}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-crisp-orange transition-colors ${
                errors.first_name && touched.first_name
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-300 focus:border-crisp-orange"
              }`}
              disabled={loading}
              aria-required="true"
              aria-invalid={
                errors.first_name && touched.first_name ? "true" : "false"
              }
              aria-describedby={
                errors.first_name && touched.first_name
                  ? "first_name-error"
                  : undefined
              }
            />
            {errors.first_name && touched.first_name && (
              <p
                id="first_name-error"
                className="mt-1 text-sm text-red-600"
                role="alert"
              >
                {errors.first_name}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="last_name"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="last_name"
              type="text"
              value={customer.last_name}
              onChange={(e) => handleFieldChange("last_name", e.target.value)}
              onBlur={() => handleBlur("last_name")}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-crisp-orange transition-colors ${
                errors.last_name && touched.last_name
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-300 focus:border-crisp-orange"
              }`}
              disabled={loading}
              aria-required="true"
              aria-invalid={
                errors.last_name && touched.last_name ? "true" : "false"
              }
              aria-describedby={
                errors.last_name && touched.last_name
                  ? "last_name-error"
                  : undefined
              }
            />
            {errors.last_name && touched.last_name && (
              <p
                id="last_name-error"
                className="mt-1 text-sm text-red-600"
                role="alert"
              >
                {errors.last_name}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="email"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            value={customer.email}
            onChange={(e) => updateCustomer("email", e.target.value)}
            onBlur={() => handleBlur("email")}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-crisp-orange transition-colors ${
              errors.email && touched.email
                ? "border-red-500 focus:border-red-500"
                : "border-gray-300 focus:border-crisp-orange"
            }`}
            disabled={loading}
            aria-required="true"
            placeholder="your.email@example.com"
            aria-invalid={errors.email && touched.email ? "true" : "false"}
            aria-describedby={
              errors.email && touched.email ? "email-error" : undefined
            }
          />
          {errors.email && touched.email && (
            <p
              id="email-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.email}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label
            htmlFor="phone"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            id="phone"
            type="tel"
            value={customer.phone}
            onChange={(e) => handleFieldChange("phone", e.target.value)}
            onBlur={() => handleBlur("phone")}
            placeholder="04XX XXX XXX"
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-crisp-orange transition-colors ${
              errors.phone && touched.phone
                ? "border-red-500 focus:border-red-500"
                : "border-gray-300 focus:border-crisp-orange"
            }`}
            disabled={loading}
            aria-required="true"
            aria-invalid={errors.phone && touched.phone ? "true" : "false"}
            aria-describedby={
              errors.phone && touched.phone
                ? "phone-error phone-help"
                : "phone-help"
            }
          />
          {errors.phone && touched.phone && (
            <p
              id="phone-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.phone}
            </p>
          )}
          <p id="phone-help" className="text-xs text-gray-500 mt-1">
            Format: 04XX XXX XXX (10 digits)
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Contact Method *
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="email"
                checked={customer.contact_preference === "email"}
                onChange={(e) =>
                  updateCustomer("contact_preference", e.target.value)
                }
                className="mr-2"
                disabled={loading}
              />
              Email
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="phone"
                checked={customer.contact_preference === "phone"}
                onChange={(e) =>
                  updateCustomer("contact_preference", e.target.value)
                }
                className="mr-2"
                disabled={loading}
              />
              Phone
            </label>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Router Requirements *
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={customer.router_required === true}
                onChange={() => updateCustomer("router_required", true)}
                className="mr-2"
                disabled={loading}
              />
              I require a router
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={customer.router_required === false}
                onChange={() => updateCustomer("router_required", false)}
                className="mr-2"
                disabled={loading}
              />
              I'll bring my own router
            </label>
          </div>
        </div>

        <div className="mb-6">
          <label
            htmlFor="referral_source"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            How did you hear about us?
          </label>
          <select
            id="referral_source"
            value={customer.referral_source ?? ""}
            onChange={(e) => updateCustomer("referral_source", e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-crisp-orange focus:border-crisp-orange transition-colors bg-white"
            disabled={loading}
            aria-label="How did you hear about us?"
          >
            <option value="">Please select...</option>
            {REFERRAL_SOURCES.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="flex items-start">
            <input
              type="checkbox"
              // Opt-out checkbox: ticking it sets marketing_opt_in to false.
              // marketing_opt_in still means "customer consented to marketing" downstream.
              checked={!customer.marketing_opt_in}
              onChange={(e) =>
                updateCustomer("marketing_opt_in", !e.target.checked)
              }
              className="mt-1 mr-3 h-4 w-4 text-crisp-orange focus:ring-crisp-orange border-gray-300 rounded"
              disabled={loading}
            />
            <span className="text-sm text-gray-700">
              I would <strong>not</strong> like to receive marketing
              communications, special offers, and updates from CRISP Wireless
            </span>
          </label>
        </div>

        <div className="mb-6">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={customer.terms_accepted || false}
              onChange={(e) =>
                updateCustomer("terms_accepted", e.target.checked)
              }
              className="mt-1 mr-3 h-4 w-4 text-crisp-orange focus:ring-crisp-orange border-gray-300 rounded"
              disabled={loading}
              required
            />
            <span className="text-sm text-gray-700">
              I agree to the{" "}
              <a
                href="https://www.crispwireless.com.au/standard-terms-and-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crisp-orange hover:underline font-medium"
              >
                Terms and Conditions
              </a>{" "}
              <span className="text-red-500">*</span>
            </span>
          </label>
          {!customer.terms_accepted && touched.phone && (
            <p className="mt-1 text-sm text-red-600" role="alert">
              You must accept the terms and conditions to proceed
            </p>
          )}
        </div>

        <div className="text-xs text-gray-500 mb-6">
          This site is protected by reCAPTCHA and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            className="text-crisp-orange hover:underline"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            className="text-crisp-orange hover:underline"
          >
            Terms of Service
          </a>{" "}
          apply.
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="flex-1 bg-gray-200 text-gray-700 font-bold py-3 px-6 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
          >
            ← Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-crisp-orange text-white font-bold py-3 px-6 rounded-lg hover:bg-opacity-90 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            aria-busy={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Submitting...
              </span>
            ) : (
              manualQualification ? "Submit Enquiry" : "Submit Order"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DetailsStep;
